import React, { useEffect, useRef, useState } from 'react';
import { Alert, CircularProgress } from '@mui/material';

import navOnlineService from '../../service/navOnline.service';
import { InputUI, ButtonUI } from '../Interface';

const parseAddresses = (addresses, input) => {
  const values = [...new Set([...addresses, ...input.split(/[,;\s]+/).filter(Boolean)]
    .map((value) => value.trim().toLowerCase()))].sort();
  if (values.length > 100) throw new Error('Legfeljebb 100 címzett adható meg.');
  if (values.some((value) => value.length > 254 || !/^[^\s@<>;,"\r\n]+@[^\s@<>;,"\r\n]+\.[^\s@<>;,"\r\n]+$/.test(value))) {
    throw new Error('Érvénytelen email-cím. Ellenőrizd a megadott címzetteket.');
  }
  return values;
};

const NavOnlineNotificationRecipients = ({ canEdit = false }) => {
  const [emails, setEmails] = useState([]);
  const [savedEmails, setSavedEmails] = useState([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [feedback, setFeedback] = useState(null);
  const [saving, setSaving] = useState(false);
  const [loadAttempt, setLoadAttempt] = useState(0);
  const mounted = useRef(false);
  const busy = useRef(false);

  useEffect(() => {
    mounted.current = true;
    const controller = new AbortController();
    setLoading(true);
    setLoadError('');
    navOnlineService.getNotificationRecipients(controller.signal)
      .then((data) => {
        if (controller.signal.aborted) return;
        const addresses = data.map(({ email }) => email).sort();
        setEmails(addresses);
        setSavedEmails(addresses);
      })
      .catch((error) => {
        if (!controller.signal.aborted) setLoadError(error?.response?.data?.error?.message || 'A NAV-értesítési címzettek betöltése sikertelen.');
      })
      .finally(() => { if (!controller.signal.aborted) setLoading(false); });
    return () => { mounted.current = false; controller.abort(); };
  }, [loadAttempt]);

  const addAddresses = () => {
    if (!canEdit || busy.current) return;
    try {
      setEmails(parseAddresses(emails, input));
      setInput('');
      setFeedback(null);
    } catch (error) { setFeedback({ severity: 'error', message: error.message }); }
  };

  const save = async () => {
    if (!canEdit || busy.current || loading || loadError) return;
    let addresses;
    try { addresses = parseAddresses(emails, input); }
    catch (error) { setFeedback({ severity: 'error', message: error.message }); return; }
    busy.current = true;
    setSaving(true);
    setFeedback(null);
    try {
      const data = await navOnlineService.saveNotificationRecipients(addresses);
      if (!mounted.current) return;
      const saved = data.map(({ email }) => email).sort();
      setEmails(saved);
      setSavedEmails(saved);
      setInput('');
      setFeedback({ severity: 'success', message: 'A NAV-értesítési címzettek mentése sikeres.' });
    } catch (error) {
      if (mounted.current) setFeedback({ severity: 'error', message: error?.response?.data?.error?.message || 'A NAV-értesítési címzettek mentése sikertelen.' });
    } finally {
      busy.current = false;
      if (mounted.current) setSaving(false);
    }
  };

  const disabled = !canEdit || saving;
  const dirty = emails.join(';') !== savedEmails.join(';') || Boolean(input.trim());

  return (
    <div className="mt-8 border-t pt-4">
      <div className="text-sm font-medium text-labelColor mb-3">NAV-hibaértesítések címzettjei</div>
      <Alert severity="info" className="mb-4">
        Ötpercenként összesített email készül a hibás számlakommunikációról. A küldést a rendszer SMTP-fiókja végzi,
        a levelezési beállításoktól függetlenül. Üres címzettlista esetén nincs email-értesítés.
      </Alert>
      {loading ? <div className="flex items-center gap-3"><CircularProgress size={24} />Címzettek betöltése...</div> : loadError ? (
        <>
          <Alert severity="error">{loadError}</Alert>
          <ButtonUI type="button" text="Címzettek újratöltése" className="bg-primary mt-3" onClick={() => setLoadAttempt((value) => value + 1)} />
        </>
      ) : (
        <>
          {feedback && <Alert severity={feedback.severity} className="mb-3">{feedback.message}</Alert>}
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-full md:w-96">
              <InputUI name="navNotificationEmail" label="Értesítési email-címek" variant="standard" value={input}
                onChange={(event) => setInput(event.target.value)} disabled={disabled}
                helperText="Több címet vesszővel vagy pontosvesszővel választhatsz el."
                onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); addAddresses(); } }} />
            </div>
            <ButtonUI type="button" text="Címzett hozzáadása" className="bg-primary" disabled={disabled || !input.trim()} onClick={addAddresses} />
          </div>
          {emails.length ? (
            <ul className="my-4 space-y-2">
              {emails.map((email) => <li key={email} className="flex items-center gap-3">
                <span>{email}</span>
                <ButtonUI type="button" text="Eltávolítás" aria-label={`${email} eltávolítása`} className="bg-primary"
                  disabled={disabled} onClick={() => { setEmails((values) => values.filter((value) => value !== email)); setFeedback(null); }} />
              </li>)}
            </ul>
          ) : <p className="my-4 text-sm text-labelColor">Nincs beállított értesítési címzett.</p>}
          <div className="flex flex-wrap gap-3">
            <ButtonUI type="button" text={saving ? 'Címzettek mentése...' : 'Értesítési címzettek mentése'} className="bg-primary"
              disabled={disabled || !dirty} onClick={save} />
            <ButtonUI type="button" text="Címzettmódosítások elvetése" className="bg-primary" disabled={disabled || !dirty}
              onClick={() => { setEmails(savedEmails); setInput(''); setFeedback(null); }} />
          </div>
        </>
      )}
    </div>
  );
};

export default NavOnlineNotificationRecipients;
